<template>
	<v-card>
		<v-card-title class="d-flex align-center justify-space-between">
			{{ t("IMPORT_DATA_LAYER") }}
			<v-btn icon variant="flat" :aria-label="t('CANCEL')" @click="emit('close')">
				<v-icon> mdi-close </v-icon>
			</v-btn>
		</v-card-title>
		<v-container style="width: 95%" class="my-2 mx-auto">
			<v-table density="comfortable" class="assignment-table">
				<thead>
					<tr>
						<th class="text-left">{{ t("FILE_NAME") }}</th>
						<th v-for="cellRole in roleColumns" :key="cellRole" class="text-center role-column">
							{{ t(roleLabelKeys[cellRole]) }}
						</th>
					</tr>
				</thead>
				<tbody>
					<!-- 1 行の 2 セルが 1 つの排他選択になるため tr を radiogroup として公開する -->
					<tr v-for="(file, index) in props.files" :key="index" role="radiogroup"
						:aria-label="file.name">
						<td class="file-name-cell">{{ file.name }}</td>
						<td v-for="cellRole in roleColumns" :key="cellRole"
							class="text-center role-column role-cell" :data-role="cellRole" role="radio"
							:aria-checked="isChecked(index, cellRole)"
							:aria-label="`${file.name}: ${t(roleLabelKeys[cellRole])}`" tabindex="0"
							@click="assign(toFileIndex(index), cellRole)"
							@keydown.enter.prevent="assign(toFileIndex(index), cellRole)"
							@keydown.space.prevent="assign(toFileIndex(index), cellRole)">
							<v-icon>{{ isChecked(index, cellRole) ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
						</td>
					</tr>
				</tbody>
			</v-table>
			<!-- 取込単位は 2 ファイル共通の 1 値。無効化する条件は持たない（AC-46 / AC-51）。
			     欄は点群アップロードと同じ共通コンポーネントを使う -->
			<distance-unit-field class="mt-4" :model-value="distanceUnit" mandatory
				@update:model-value="selectDistanceUnit" />
			<v-row class="mt-4">
				<v-col data-test="dialog-actions" class="d-flex justify-end ga-2">
					<v-btn variant="text" rounded @click="emit('close')">{{ t("CANCEL") }}</v-btn>
					<v-btn variant="text" rounded @click="emit('previous')">{{ t("PREVIOUS") }}</v-btn>
					<v-btn color="primary" rounded @click="confirm">{{ t("UPLOAD") }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script setup lang="ts">
import DistanceUnitField from "@/components/AddData/DistanceUnitField.vue";
import {
	type DtmOrthoImportSelection,
	type DtmOrthoRole,
	type ImageFileIndex,
	type NamedFile,
	dtmOrthoRoles,
} from "@/components/AddData/dtm-ortho/dtmOrtho.types";
import type { DistanceUnit } from "@/models/siteUnit.types";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
	/** 表示に使うのは name だけ。端末上の File も外部ストレージのノード参照も受ける（GI-13） */
	files: readonly [NamedFile, NamedFile];
	/** 取込単位の初期値。現場の単位設定の解決は facade が行う（AC-47） */
	initialDistanceUnit: DistanceUnit;
}>();

const emit = defineEmits<{
	(e: "confirm", selection: DtmOrthoImportSelection): void;
	(e: "previous"): void;
	(e: "close"): void;
}>();

const { t } = useI18n();

/** 種別の列（表示順）と見出しの i18n キー */
const roleColumns = [dtmOrthoRoles.image, dtmOrthoRoles.terrain] as const;
const roleLabelKeys: Readonly<Record<DtmOrthoRole, string>> = {
	[dtmOrthoRoles.image]: "IMAGE",
	[dtmOrthoRoles.terrain]: "TERRAIN",
};

// 「どちらが画像か」の 1 値だけを持つため、両方画像 / 両方地形は表現できない（AC-6）。
// 初期値は選択順の 1 ファイル目を画像にする（AC-7）。ファイル名からは判定しない（AC-8）
const imageIndex = ref<ImageFileIndex>(0);

/** 取込単位。初期値は prop で受け取り、ダイアログは store を参照しない（AC-47） */
const distanceUnit = ref<DistanceUnit>(props.initialDistanceUnit);

/** 欄は mandatory なので未選択は来ないが、来ても選択を保持する（1 値である型を崩さない） */
const selectDistanceUnit = (value: DistanceUnit | undefined): void => {
	if (value !== undefined) distanceUnit.value = value;
};

const toFileIndex = (index: number): ImageFileIndex => (index === 0 ? 0 : 1);

/** もう一方の行（2 行しかないため画像の反対が地形になる。AC-6） */
const otherIndex = (index: ImageFileIndex): ImageFileIndex =>
	index === 0 ? 1 : 0;

const isImage = (index: number): boolean => imageIndex.value === index;

/** その行のその種別が選択状態か */
const isChecked = (index: number, role: DtmOrthoRole): boolean =>
	role === dtmOrthoRoles.image ? isImage(index) : !isImage(index);

const assign = (index: ImageFileIndex, role: DtmOrthoRole): void => {
	// 地形を選んだ行は、もう一方の行が画像になる（AC-6）
	imageIndex.value = role === dtmOrthoRoles.image ? index : otherIndex(index);
};

const confirm = (): void => {
	emit("confirm", {
		assignment: {
			imageFile: props.files[imageIndex.value],
			terrainFile: props.files[otherIndex(imageIndex.value)],
		},
		distanceUnit: distanceUnit.value,
	});
};
</script>

<style scoped>
.assignment-table {
	width: 100%;
}

.role-column {
	width: 96px;
}

.role-cell {
	cursor: pointer;
}

/* 長いファイル名も全体が読めるよう折り返す（AC-32） */
.file-name-cell {
	word-break: break-all;
	white-space: normal;
}
</style>
